// Desk booking with several services in one visit (one booking, one line per service). Staff pick the
// services from the branch's list and a day; the server offers the ways to fit them: one stylist back to back,
// back to back with more than one stylist, or (together) at the same time with different stylists. The customer
// gets one confirmation and one set of reminders naming every service.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api, { addDays, clockLabel, isoDate } from '../services/api';
import { useEngagementScope } from '../context/EngagementScope';
import BranchSelect from './BranchSelect';
import { Modal, Spinner } from './ui';

const digits = (v) => String(v || '').replace(/\D/g, '');

/** prefill: { phone, name, gender, branch, day, serviceIds } carried over from the single-service form. */
export default function MultiBookingModal({ prefill = {}, onClose, onBooked }) {
  const scope = useEngagementScope();
  const soleBranch = scope.selectedIds.length === 1 ? scope.selectedIds[0] : '';
  const pickBranch = scope.scopeType !== 'franchise' && scope.branches.length > 1 && !soleBranch;
  const [branch, setBranch] = useState(prefill.branch || soleBranch);
  const franchiseId = pickBranch ? branch : soleBranch;
  const [phone, setPhone] = useState(prefill.phone || '');
  const [name, setName] = useState(prefill.name || '');
  const [gender, setGender] = useState(prefill.gender || '');
  const [services, setServices] = useState([]);
  const [lines, setLines] = useState([
    ...(prefill.serviceIds || []).map((id) => ({ service_id: id, for_name: '' })),
    ...Array.from({ length: Math.max(1, 2 - (prefill.serviceIds || []).length) }, () => ({ service_id: '', for_name: '' })),
  ]);
  const [day, setDay] = useState(prefill.day || isoDate());
  const [time, setTime] = useState('');
  const [together, setTogether] = useState(false);
  const [options, setOptions] = useState(null);
  const [kinds, setKinds] = useState({});
  const [chosen, setChosen] = useState(null);
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const autoName = useRef('');

  useEffect(() => {
    if (pickBranch && !branch) return;
    api
      .listServices(pickBranch ? branch : undefined)
      .then((res) => setServices(res?.services || []))
      .catch(() => setServices([]));
  }, [branch, pickBranch]);

  useEffect(() => {
    const d = digits(phone);
    if (d.length < 10) return undefined;
    const t = setTimeout(() => {
      api
        .lookupCustomer(d)
        .then((res) => {
          const filled = res.found ? res.name || '' : '';
          setName((n) => (!n.trim() || n === autoName.current ? filled : n));
          autoName.current = filled;
          if (res.found && res.gender) setGender((g) => g || res.gender);
        })
        .catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [phone]);

  const picked = lines.filter((l) => l.service_id);
  const shownServices = useMemo(
    () =>
      gender
        ? services.filter((s) => {
            const forWho = (s.for || []).map((g) => String(g).toLowerCase());
            return !forWho.length || forWho.includes('everyone') || forWho.includes(gender);
          })
        : services,
    [services, gender],
  );

  useEffect(() => {
    setChosen(null);
    if (!picked.length || (pickBranch && !branch)) {
      setOptions(null);
      return;
    }
    setOptions(null);
    api
      .bookingOptions({ day, count: picked.length, together: together ? 'true' : '', time: time || undefined, franchise_id: franchiseId || undefined })
      .then((d) => {
        setOptions((d.options || []).slice(0, 30));
        setKinds(d.kinds || {});
      })
      .catch((e) => toast.error(e.message));
  }, [day, time, together, picked.length, branch]);

  const setLine = (i, patch) => setLines((list) => list.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const ready = digits(phone).length >= 10 && gender && chosen && picked.length;

  const book = async (allowOverlap = false) => {
    setSaving(true);
    try {
      const payload = {
        phone: digits(phone),
        customer_name: name.trim(),
        gender,
        franchise_id: franchiseId ? Number(franchiseId) : null,
        notify,
        allow_overlap: allowOverlap,
        services: picked.map((l, i) => {
          const svc = services.find((s) => String(s.service_id) === String(l.service_id));
          return { service_id: svc?.service_id || null, service_name: svc?.name || '', slot_id: chosen.lines[i].slot_id, for_name: l.for_name.trim() };
        }),
      };
      const res = await api.createBooking(payload);
      toast.success(`Booked ${picked.length} services${notify ? '; one WhatsApp confirmation sent' : ''}`);
      onBooked(res.booking);
      onClose();
    } catch (e) {
      if (e.code === 'overlap' && window.confirm(`${e.message}\n\nBook anyway?`)) {
        setSaving(false);
        book(true);
        return;
      }
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Book appointment · several services" onClose={onClose} size="wide">
      <div className="de-form-grid de-form-grid--2">
        {pickBranch ? (
          <div className="de-field" style={{ gridColumn: '1 / -1' }}>
            <span className="de-label">Branch *</span>
            <BranchSelect branches={scope.branches} value={branch} onChange={setBranch} placeholder="Choose the branch…" />
          </div>
        ) : null}
        <label className="de-field">
          <span className="de-label">Mobile *</span>
          <input className="de-input de-input--bordered" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile" />
        </label>
        <label className="de-field">
          <span className="de-label">Name</span>
          <input className="de-input de-input--bordered" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="de-field">
          <span className="de-label">Booking for *</span>
          <select className="de-select" value={gender} onChange={(e) => setGender(e.target.value)}>
            <option value="">Male or female?</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
          </select>
        </label>
      </div>

      <div className="de-section-label de-mt-16">Services</div>
      {lines.map((l, i) => (
        <div key={i} className="de-row" style={{ gap: 8, marginBottom: 6 }}>
          <span className="de-small de-muted" style={{ width: 18, alignSelf: 'center' }}>
            {i + 1}.
          </span>
          <select className="de-select" style={{ flex: 2 }} value={l.service_id} onChange={(e) => setLine(i, { service_id: e.target.value })}>
            <option value="">Choose a service…</option>
            {shownServices.map((s) => (
              <option key={s.service_id} value={s.service_id}>
                {s.name}
                {s.price_text ? ` · ${s.price_text}` : ''}
              </option>
            ))}
          </select>
          <input className="de-input de-input--bordered" style={{ flex: 1 }} value={l.for_name} onChange={(e) => setLine(i, { for_name: e.target.value })} placeholder="For someone else? Their name" />
          <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={lines.length <= 1} onClick={() => setLines((list) => list.filter((_, j) => j !== i))}>
            ✕
          </button>
        </div>
      ))}
      {lines.length < 6 ? (
        <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setLines((list) => [...list, { service_id: '', for_name: '' }])}>
          + Another service
        </button>
      ) : null}

      <div className="de-section-label de-mt-16">When</div>
      <div className="de-row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setDay(addDays(day, -1))} disabled={day <= isoDate()}>
          ←
        </button>
        <input type="date" className="de-input de-input--bordered" style={{ width: 170 }} value={day} min={isoDate()} onChange={(e) => setDay(e.target.value)} />
        <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setDay(addDays(day, 1))}>
          →
        </button>
        <input type="time" className="de-input de-input--bordered" style={{ width: 130 }} value={time} onChange={(e) => setTime(e.target.value)} title="Only options starting at this time (optional)" />
        <label className="de-row" style={{ gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={together} onChange={(e) => setTogether(e.target.checked)} />
          <span className="de-small">At the same time with different stylists</span>
        </label>
      </div>
      <div className="de-mt-8">
        {!picked.length ? (
          <div className="de-small de-muted">Choose the services first.</div>
        ) : !options ? (
          <div className="de-loader">
            <Spinner /> Finding times…
          </div>
        ) : options.length ? (
          <div style={{ display: 'grid', gap: 6, maxHeight: 280, overflow: 'auto' }}>
            {options.map((o, i) => (
              <label key={i} className="de-card" style={{ padding: 10, cursor: 'pointer', outline: chosen === o ? '2px solid var(--de-gold)' : 'none' }}>
                <input type="radio" name="bundle" checked={chosen === o} onChange={() => setChosen(o)} style={{ marginRight: 8 }} />
                <b>
                  {o.label} – {clockLabel(o.ends)}
                </b>
                <span className="de-small de-muted"> · {kinds[o.kind] || o.kind}</span>
                <div className="de-small" style={{ marginLeft: 24 }}>
                  {o.lines
                    .map((ln, j) => {
                      const svc = services.find((s) => String(s.service_id) === String(picked[j]?.service_id));
                      return `${svc?.name || 'Service'} ${ln.label} with ${ln.with}`;
                    })
                    .join(' → ')}
                </div>
              </label>
            ))}
          </div>
        ) : (
          <div className="de-callout">No free times that fit all the services that day. Try another day or fewer services.</div>
        )}
      </div>

      <label className="de-row de-mt-16" style={{ gap: 6, alignItems: 'center' }}>
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
        <span className="de-small">Send the WhatsApp confirmation and reminders (one set for the whole booking)</span>
      </label>
      <div className="de-form-actions" style={{ marginTop: 16 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--primary" disabled={!ready || saving} onClick={() => book(false)}>
          {saving ? <Spinner light /> : null}
          Book {picked.length || ''} service{picked.length === 1 ? '' : 's'}
        </button>
      </div>
    </Modal>
  );
}
